import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Stack, Typography } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useAuth, type AuthResponse } from '@/hooks/useAuth';

export default function EmailVerificationForm() {
  const { confirmEmailVerification } = useAuth();
  const [params] = useSearchParams();
  const key = params.get('key') || '';
  const [attempt, setAttempt] = useState<number>(0);
  const [verifiedKey, setVerifiedKey] = useState<string>('');
  const [pending, setPending] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const cached = useRef<{ id: string; result: Promise<AuthResponse> } | null>(null);
  useEffect(() => {
    if (!key) return;
    let active = true;
    setPending(true);
    setError(null);
    const id = `${key}:${attempt}`;
    // StrictMode subscribes twice to one confirmation instead of redeeming the key twice.
    if (cached.current?.id !== id) cached.current = { id, result: confirmEmailVerification({ key }) };
    void cached.current.result
      .then((result) => {
        if (!active) return;
        if (result.success) setVerifiedKey(key);
        else setError('This verification link could not be verified. Retry or request a new link.');
      })
      .catch(() => {
        if (active) setError('Could not verify this link. Please retry.');
      })
      .finally(() => {
        if (active) setPending(false);
      });
    return () => {
      active = false;
    };
  }, [key, attempt, confirmEmailVerification]);
  const verified = !!key && verifiedKey === key;
  return (
    <Box sx={{ maxWidth: 640, mx: 'auto', p: { xs: 2, md: 3 } }}>
      <Card>
        <CardContent>
          <Stack spacing={3}>
            <Typography variant="h4" component="h1">
              {verified ? 'Email Verified' : 'Verify Your Email'}
            </Typography>
            {verified ? (
              <>
                <Alert severity="success">Your email address has been verified.</Alert>
                <Button component="a" href="/" variant="contained">
                  Continue
                </Button>
              </>
            ) : (
              <>
                <Typography>
                  {key
                    ? 'Confirming your email address.'
                    : 'Check your inbox for a verification link, or manage your email addresses to request a new one.'}
                </Typography>
                {error && <Alert severity="error">{error}</Alert>}
                {key && (
                  <Button
                    onClick={() => setAttempt((value) => value + 1)}
                    disabled={pending}
                    loading={pending}
                    loadingPosition="start"
                  >
                    {pending ? 'Verifying Email…' : 'Try Again'}
                  </Button>
                )}
                <Button component="a" href="/accounts/email/" variant="outlined">
                  Manage Email Addresses
                </Button>
                <Button component="a" href="/accounts/login/">
                  Sign In
                </Button>
              </>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
